/* eslint-disable react/no-unstable-nested-components */
import {
  SignInInput, SignUpInput,
  signIn,
  signUp,
} from '@aws-amplify/auth';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
// eslint-disable-next-line import/no-unresolved
import '@aws-amplify/ui-react/styles.css';
import { Box } from '@mui/material';
import { I18n } from 'aws-amplify/utils';
import App from '../App';
import HeaderContainer from '../App/Components/Header/styles';
import AmplifyContextProvider from '../App/Contexts/AmplifyContextProvider';
import useLanguageUpdate from '../App/Hooks/useLanguageUpdate';
import useRefreshTokens from '../App/Hooks/useRefreshTokens';
import { CookieProvider } from '../App/Hooks/useUserInfo';
import LoadingBackdrop from '../Common/LoadingBackdrop';
import SignInHeader from './Headers/SignInHeader';
import SignUpHeader from './Headers/SignUpHeader';
import SignInFooter from './SignInFooter';
import { SingleSignOnContextProvider } from './SingleSignOnContext';
import logoSrc from './logo.svg';
import StyledBox from './styles';
import theme from './theme';
import { validateOrganization, validatePassword } from './validateSignupForm';

export default function AuthenticatedApp() {
  const { hash } = window.location;
  useLanguageUpdate();
  const initialState = hash === '#/signup' ? 'signUp' : 'signIn';
  const storedEmail = localStorage.getItem('email') ?? '';
  const initializing = useRefreshTokens();

  if (initializing) {
    return <LoadingBackdrop />;
  }

  return (
    <StyledBox>
      <SingleSignOnContextProvider>
        <ThemeProvider theme={theme}>
          <Authenticator
            initialState={initialState}
            formFields={{
              signIn: {
                password: {
                  labelHidden: true,
                  placeholder: I18n.get('login.password'),
                },
                username: {
                  defaultValue: storedEmail,
                  labelHidden: true,
                  placeholder: I18n.get('login.email'),
                } as any,
                rememberMe: {
                  label: I18n.get('login.remember_me'),
                  type: 'checkbox',
                  id: 'rememberMe',
                  defaultChecked: !!storedEmail,
                } as any,
              },
              signUp: {
                'custom:organisationId': {
                  order: -1,
                  labelHidden: true,
                  placeholder: I18n.get('signup.organisation'),
                },
                password: {
                  placeholder: I18n.get('signup.password'),
                },
                email: {
                  placeholder: I18n.get('signup.email'),
                },
                confirm_password: {
                  placeholder: I18n.get('signup.confirm_password'),
                },
              },
            }}
            components={{
              Header() {
                return (
                  <HeaderContainer id="login-header">
                    <Box>
                      <img src={logoSrc} alt="logo" />
                    </Box>
                    <div />
                  </HeaderContainer>
                );
              },
              SignIn: {
                Header: SignInHeader,
                Footer: SignInFooter,
              },
              SignUp: {
                Header: SignUpHeader,
              },
            }}
            services={{
              async validateFormPassword(formData) {
                if (!validatePassword(formData.password) && ((formData.password ?? '') !== '')) {
                  return {
                    password: I18n.get('signup.password_error'),
                  };
                }
                return Promise.resolve();
              },
              async validateCustomSignUp(formData) {
                if (!validateOrganization(formData['custom:organisationId']) && ((formData['custom:organisationId'] ?? '') !== '')) {
                  return {
                    'custom:organisationId': I18n.get('signup.organisation_error'),
                  };
                }
                return Promise.resolve();
              },
              async handleSignUp(input: SignUpInput) {
                const { username } = input;
                const lowerCaseEmail = username.toLowerCase();
                const updatedInput = {
                  ...input,
                  username: lowerCaseEmail,
                  options: {
                    userAttributes: {
                      ...input.options?.userAttributes,
                      email: lowerCaseEmail,
                    },
                  },
                };
                return signUp(updatedInput);
              },
              async handleSignIn(input: SignInInput) {
                const { username } = input;
                const emailValue = username.toLowerCase().trim();
                const rememberMeCheckbox = document.querySelector('#rememberMe');
                if (rememberMeCheckbox && (rememberMeCheckbox as HTMLInputElement).checked) {
                  localStorage.setItem('email', emailValue);
                } else {
                  localStorage.removeItem('email');
                }
                return signIn({
                  ...input,
                  username: emailValue,
                  options: { authFlowType: 'USER_SRP_AUTH' },
                });
              },
            }}
            loginMechanisms={['email']}
          >
            {({ signOut, user }) => (
              <CookieProvider>
                <AmplifyContextProvider signOut={signOut} user={user}>
                  <App />
                </AmplifyContextProvider>
              </CookieProvider>
            )}
          </Authenticator>
        </ThemeProvider>
      </SingleSignOnContextProvider>
    </StyledBox>
  );
}
